import { TDashboardStatus } from "src/app/pages/dashboard/dashboard.constants";
import { IPersonDataIsv } from "../../isv/components/beneficiary-change-isv/beneficiary-change-isv.constants";
import { EmployerTableRow } from "../../isv/components/spostamento-iscritto/spostamento-iscritto.constants";
import { IInvestmentLine, IPolicyAsset } from "./policy.model";


export interface IKpiResponse {
    athora: IKpiObject;
    generale: IKpiObject;
    intesa: IKpiObject;
    type?: 'number' | 'premium';
}

export interface IKpiObject {
    totale: string;
    versamenti: string;
    sinistri: string;
    riscatti: string;
    liquidazioni: string;
}

export interface IOperationKPI {
    label: string;
    key: string;
}

export interface IDetailedOperationKPI {
    companyId: string;
    kpis: IOperationKPI[];
}

export interface IDashboardOperation {
    id?: number;
    bperPolicyNumber?: string;
    companyPolicyNumber?: string;
    bperContractNumber?: string;
    operationDate?: string;
    effectDate?: string;
    operationId?: string;
    operationDescription?: string;
    userId?: string;
    userAbi?: string;
    companyId?: string;
    status?: string;
    showIcon?: boolean;
    benefitAtDate?: string;
    movementId?: string;
    completionDate?: string;
}

export interface PaginatedRequest {
  pageIndex?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface IDashboardOperationRequest { // Can now extend PaginatedRequest
    bperPolicyNumber?: string;
    bperContractNumber?: string;
    operationCode?: string;
    userId?: string;
    companyCode?: string;
    operationStatus?: string[];
    companyCodes?: string;
    startDate?: string;
    endDate?: string;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
    pageIndex?: number;
    pageSize?: number;
    limit?: number;
}

export interface IEmployerSearchRequest extends PaginatedRequest {
  policyNumber: string;
  socialReason?: string;
  conventionNumber?: string;
  pIva?: string;
  filterByProduct?: boolean | string;
}

export interface IEmployer {
  socialReason: string;
  pIVA: string;
  conventioned: string;
  employerCode: string;
  productCode: string;
  productConvention: string;
  registryId: string;
}


export interface IDashboardOperationResponse {
    currentPage: number;
    data: IDashboardData[];
    total: number;
    totalPages: number;
}

export interface IDashboardData {
    id: number;
    receiveDate?: string;
    effectDate?: string;
    operationStatus?: string;
    companyCode?: string;
    companyPolicyNumber?: string;
    bperPolicyNumber: string;
    bperContractNumber: string;
    premium: number;
    operationDate?: string;
    userAbi?: string;
    userAgencyCode?: string;
    userCab?: string;
    iban?: string;
    customerNdg?: string;
    userId?: string;
    operatorId?: string;
    sellerMode?: string;
    fiscalCode?: string;
    fiscalCodeLgrp?: string;
    operationTypeCode: string;
    operationTypeDescription: string;
    movementId?: string;
    operationAmount?: number;
    completionDate?: string;
}

export interface IAsset {
    dynamicValues: {
        importoPremio: string;
    },
    roles: IRole[],
    assetId: number;
    assetDescription: string;
    assetCode: string;
}

export interface IRole {
    role: string;
    fiscalCode: string;
}

export interface IQuestionnaire {
    fiscalCode: string;
    questionnaireCode: string;
    questionnaireDescription: string;
    questions: IQuestion[];
}

export interface IQuestion {
    questionCode: string;
    questionDescription: string;
    answerValues: string[];
    parentQuestions: IParentQuestion[];
    isHidden?: boolean;
    defaultAnswer?: string;
    isInternal?: boolean;
}

export interface IParentQuestion {
    code: string;
    answerEnablingValues: string[];
}

export interface IQuestionnaireAnswers {
    questionnaireCode: string;
    fiscalCode: string;
    answers: any;
}

export interface IFilledQuestionnaire {
    questionnaireForm: any;
    fiscalCode: string;
    questionnaireCode?: string;
}

export interface IDraft {
    policyNumber?: string;
    contractNumber: string;
    operationCode: string;
    operationState: TDashboardStatus;
    draft: string; // IDraftItem (base64)
    hasErrored?: boolean;
    latestError?: string;
    movementId?: string;
}

export interface IDraftItem {
    firstStepForm: any;
    firstStepFormTouched?: boolean;
    secondStepForm?: any;
    questionnaireForm?: any;
    selectedIndex: number;
    assets?: IPolicyAsset[];
    funds?: IPolicyAsset[];
    ndgVariationForm?: any;
    searchedSubject?: any;
    beneficiaries?: any;
    questionnairesArray?: IFilledQuestionnaire[];
    searchedTutor?: any;
    customerHasTutor?: boolean;
    thirdParty?: IPersonDataIsv;
    currentBranchOffice?: string;
    additionalForm?: any;
    effectiveOwnersList?: any[];
    contractorRegistryForm?: any;
    contractorResidenceForm?: any;
    contractorAddressForm?: any;
    contractorIdentificationDocumentForm?: any;
    contractorBusinessForm?: any;
    newContractorConsentsForm?: any;
    operationDataForm?: any;
    operatorDataForm?: any;
    insuredForm?: any;
    beneficiaryForm?: any;
    activeFunds?: IPolicyAsset[];
    investmentFunds?: IPolicyAsset[];
    lines?: IInvestmentLine[];
    rebalancedLine?: IInvestmentLine;
    employersData?: EmployerTableRow[];
    newContractorData?: any;
    searchedLegalGuardian?: any;
    newContractorSearched?: boolean;
    newContractorProperVerificationForm?: any;
    operationEffectDate?: string;
    selectedRedemptionType?: string;
    legalGuardianRegistryForm?: any;
    legalGuardianResidenceForm?: any;
    legalGuardianAddressForm?: any;
    legalGuardianIdentificationDocumentForm?: any;
    legalGuardianProperVerificationForm?: any;
    liquidationSubjects?: any;
}

export interface IPolicyDocument {
	esito: string,
	descrizioneEsito: string,
	fileContent: string;
}

export interface IOperationsConstraints {
    [key: string]: IProductConstraint[];
}

export interface IProductConstraint {
    role: IOperationConstraint;
    code: string;
}

export interface IOperationConstraint {
    state: boolean;
    message: string;
    constraint?: IConstraint[];
}

export interface IConstraint {
    values: number;
    operator: string;
    reference: string;
}

export const CONSTRAINT_MESSAGE_1: string = "Il premio lordo deve essere ";
export const CONSTRAINT_GREATER: string = "maggiore di ";
export const CONSTRAINT_GREATER_OR_EQUAL: string = "maggiore o uguale a ";
export const CONSTRAINT_LESSER: string = "minore di ";
export const CONSTRAINT_LESSER_OR_EQUAL: string = "minore o uguale a ";
export const CONSTRAINT_EQUAL: string = "uguale a ";

export const DETTAGLIO_ANAGRAFICO_KEY: string = 'ns2:dettaglioAnagrafico';
